import { Formik } from "formik";
import { useContext } from "react";

import { addToast } from "@icg360/design-system";

import { MSSLink } from "components/common/link";
import { AuthAppContext } from "components/root/auth-app-provider";
import { validatePasswordFormik } from "consts";
import { useUpdateInsuredInfoMutation } from "gql/__generated__/hooks";
import { logException, trackEvent } from "utils";

import ChangePasswordModalForm from "./change-password-modal-form";

const changePasswordError = (
  <>
    An error occurred. Please try again, or{" "}
    <MSSLink to="/request-reset">reset your password</MSSLink> via email.
  </>
);

const initialValues = {
  password: "",
  passwordConfirmation: "",
};

export type ChangePasswordModalSchema = typeof initialValues;

type ChangePasswordModalProps = {
  show: boolean;
  handleClose: () => void;
  userEmail: string;
};

const ChangePasswordModal = ({
  show,
  handleClose,
  userEmail,
}: ChangePasswordModalProps) => {
  const { selectedPolicyId } = useContext(AuthAppContext);
  const [updatePassword] = useUpdateInsuredInfoMutation({
    context: {
      clientName: "keystone-api",
    },
  });

  const handleSubmit = async (
    { password },
    { setSubmitting, setStatus, resetForm }
  ) => {
    try {
      setSubmitting(true);
      setStatus(null);
      const { data: updatePasswordData, errors: updatePasswordErrors } =
        await updatePassword({
          variables: {
            policyID: selectedPolicyId,
            emailAddress: userEmail,
            username: userEmail,
            userUpdates: {
              password,
            },
          },
        });

      if (updatePasswordData?.updateInsuredInfo) {
        trackEvent("Update password - reset complete");
        addToast("Password has been successfully updated.", { icon: true });
        handleClose();
        resetForm();
      } else {
        setStatus(changePasswordError);
        trackEvent("Error Displayed (Change Password)", {
          errors: updatePasswordErrors,
        });
      }

      setSubmitting(false);
    } catch (err) {
      logException(err);
      setStatus(changePasswordError);
      trackEvent("Error Displayed (Change Password)", {
        error: err,
      });
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validatePasswordFormik}
      enableReinitialize
    >
      <ChangePasswordModalForm show={show} handleClose={handleClose} />
    </Formik>
  );
};

export default ChangePasswordModal;
